import { formatDate } from '@mysten/core';
import { format } from 'date-fns';

export type KlineAxisLabelMode = 'time' | 'dayTime' | 'day';

export type KlineAxisLayout = {
	tickValues: number[];
	labelMode: KlineAxisLabelMode;
};

function klineAxisLabelMode(openTimesMs: number[]): KlineAxisLabelMode {
	if (!openTimesMs.length) return 'dayTime';
	const min = Math.min(...openTimesMs);
	const max = Math.max(...openTimesMs);
	const spanMs = max - min;
	if (spanMs <= 24 * 60 * 60 * 1000) return 'time';
	if (spanMs <= 7 * 24 * 60 * 60 * 1000) return 'dayTime';
	return 'day';
}

/** Compact label for kline chart X axis; mode picks length by visible span. */
export function formatKlineAxisTime(ms: number, mode: KlineAxisLabelMode = 'dayTime'): string {
	if (!Number.isFinite(ms) || ms <= 0) return '';
	switch (mode) {
		case 'time':
			return format(new Date(ms), 'HH:mm');
		case 'day':
			return format(new Date(ms), 'M/d');
		default:
			return format(new Date(ms), 'M/d HH:mm');
	}
}

/** Richer label for chart tooltips. */
export function formatKlineTooltipTime(ms: number): string {
	if (!Number.isFinite(ms) || ms <= 0) return '--';
	return formatDate(new Date(ms), ['month', 'day', 'hour', 'minute']);
}

function labelWidthPx(mode: KlineAxisLabelMode): number {
	switch (mode) {
		case 'time':
			return 36;
		case 'day':
			return 32;
		default:
			return 52;
	}
}

function dedupeTickLabels(times: number[], mode: KlineAxisLabelMode): number[] {
	const out: number[] = [];
	let prevLabel = '';
	for (const t of times) {
		const label = formatKlineAxisTime(t, mode);
		if (!label || label === prevLabel) continue;
		out.push(t);
		prevLabel = label;
	}
	return out.length >= 2 ? out : times.slice(0, Math.min(2, times.length));
}

/** Width-aware tick selection to avoid overlapping X labels. */
export function buildKlineAxisTicks(openTimesMs: number[], chartWidth: number): KlineAxisLayout {
	const sorted = [...openTimesMs].filter((t) => t > 0).sort((a, b) => a - b);
	if (!sorted.length) return { tickValues: [], labelMode: 'dayTime' };

	const labelMode = klineAxisLabelMode(sorted);
	const plotWidth = Math.max(100, chartWidth - 60);
	const maxTicks = Math.max(2, Math.min(4, Math.floor(plotWidth / labelWidthPx(labelMode))));

	let tickValues: number[];
	if (sorted.length <= maxTicks) {
		tickValues = sorted;
	} else {
		const step = Math.max(1, Math.ceil(sorted.length / maxTicks));
		tickValues = sorted.filter((_, index) => index % step === 0);
		const last = sorted[sorted.length - 1];
		if (tickValues[tickValues.length - 1] !== last) {
			tickValues.push(last);
		}
	}

	return { tickValues: dedupeTickLabels(tickValues, labelMode), labelMode };
}
